// src/components/dnl/SparsePointsRenderer.tsx
/**
 * Sparse Points Renderer Component
 * Phase 1: Custom/Sparse Points System
 * 
 * Renders custom tick marks and labels for DNL sparse mode.
 * Handles:
 * - Custom tick marks at specified values
 * - "?" styling for unknown values
 * - Unit suffix formatting
 * - Click interactions for editing
 * 
 * Based on DNL Enhancement Specification v1.0, Section 3.1
 */

import React, { useMemo, useCallback, useState, useEffect, useRef } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { RootState } from '../../types/redux';
import { valueToPx } from '../../lib/scale';
import { formatNumberWithConfig } from '../../lib/numberFormatting';
import { selectLocale } from '../../store/slices/preferencesSlice';
import type { DNLCustomTick, UnitSuffix } from '../../types/dnl';
import type { Domain, NumberFormatConfig } from '../../types/redux';
import {
  removeCustomTick,
  toggleCustomTickLabelVisible,
  setCustomTickStyle,
} from '../../store/slices/dnlToolsSlice';

// =============================================================================
// TYPES
// =============================================================================

export interface SparsePointsRendererProps {
  /** Which line to render points for */
  line: 'primary' | 'secondary';
  /** Whether the number line is horizontal */
  isHorizontal: boolean;
  /** The visible domain */
  domain: Domain;
  /** Length of the line in pixels */
  lineLength: number;
  /** X position of the line */
  lineX: number;
  /** Y position of the line */
  lineY: number;
  /** Current scale (pixels per unit) */
  scale: number;
  /** Unit suffix configuration */
  unitSuffix: UnitSuffix;
  /** Number format configuration */
  numberFormat: NumberFormatConfig;
  /** Label position relative to line */
  labelPosition: 'above' | 'below';
  /** Handler for label click (to hide) */
  onLabelClick?: (tickId: string, event: React.MouseEvent) => void;
  /** Handler for tick right-click (context menu) */
  onTickContextMenu?: (tickId: string, event: React.MouseEvent) => void;
}

// =============================================================================
// STYLING CONSTANTS
// =============================================================================

const TICK_HEIGHT = 12;
const TICK_WIDTH = 2;
const LABEL_OFFSET = 20;
const QUESTION_MARK_SIZE = 24;
const HIGHLIGHTED_TICK_HEIGHT = 16;

// Colors
const NORMAL_COLOR = '#374151'; // gray-700
const NORMAL_COLOR_DARK = '#D1D5DB'; // gray-300
const QUESTION_COLOR = '#F59E0B'; // amber-500
const HIGHLIGHTED_COLOR = '#3B82F6'; // blue-500

// =============================================================================
// HELPER FUNCTIONS
// =============================================================================

/**
 * Format the label for a custom tick
 */
function formatTickLabel(
  tick: DNLCustomTick, 
  unitSuffix: UnitSuffix,
  numberFormat: NumberFormatConfig,
  line: 'primary' | 'secondary',
  scaleFactor: { n: number; d: number } | undefined,
  locale: string
): string {
  // If tick is a question mark, return "?"
  if (tick.style === 'question') {
    return '?';
  }

  // If tick has a custom label, use it
  if (tick.label) {
    return tick.label;
  }

  // For secondary line ticks, convert domain value to secondary value
  // Custom ticks store values in domain (primary) coordinate system
  let displayValue = tick.value;
  if (line === 'secondary' && scaleFactor && (tick.line === 'secondary' || tick.line === 'both')) {
    const factor = scaleFactor.n / scaleFactor.d;
    displayValue = tick.value * factor;
  }

  // Use the comprehensive number formatter
  const formattedValue = formatNumberWithConfig(displayValue, numberFormat, locale as any);

  // Add unit suffix if enabled
  if (unitSuffix.enabled && unitSuffix.text) {
    return unitSuffix.position === 'before'
      ? `${unitSuffix.text}${formattedValue}`
      : `${formattedValue}${unitSuffix.text}`;
  }

  return formattedValue;
}

// =============================================================================
// SUB-COMPONENTS
// =============================================================================

/**
 * Individual tick mark component
 */
const TickMark: React.FC<{
  x: number;
  y: number;
  isHorizontal: boolean;
  style: 'normal' | 'highlighted' | 'question';
  colour?: string;
  isDark: boolean;
}> = ({ x, y, isHorizontal, style, colour, isDark }) => {
  const height = style === 'highlighted' ? HIGHLIGHTED_TICK_HEIGHT : TICK_HEIGHT;
  const color = colour || (
    style === 'question'
      ? QUESTION_COLOR
      : style === 'highlighted'
        ? HIGHLIGHTED_COLOR
        : isDark
          ? NORMAL_COLOR_DARK
          : NORMAL_COLOR
  );

  if (isHorizontal) {
    return (
      <line
        x1={x}
        y1={y - height / 2}
        x2={x}
        y2={y + height / 2}
        stroke={color}
        strokeWidth={style === 'question' ? 3 : TICK_WIDTH}
        strokeLinecap="round"
      />
    );
  } else {
    return (
      <line
        x1={x - height / 2}
        y1={y}
        x2={x + height / 2}
        y2={y}
        stroke={color}
        strokeWidth={style === 'question' ? 3 : TICK_WIDTH}
        strokeLinecap="round"
      />
    );
  }
};

/**
 * Question mark label component with special styling
 */
const QuestionMarkLabel: React.FC<{
  x: number;
  y: number;
  isHorizontal: boolean;
  labelPosition: 'above' | 'below';
  onClick?: (e: React.MouseEvent) => void;
}> = ({ x, y, isHorizontal, labelPosition, onClick }) => {
  const offsetY = labelPosition === 'above' ? -LABEL_OFFSET : LABEL_OFFSET;
  
  return (
    <g
      transform={`translate(${isHorizontal ? x : x + offsetY}, ${isHorizontal ? y + offsetY : y})`}
      onClick={onClick}
      style={{ cursor: onClick ? 'pointer' : 'default' }}
    >
      <circle
        cx={0}
        cy={0}
        r={QUESTION_MARK_SIZE / 2}
        fill={QUESTION_COLOR}
        opacity={0.2}
      />
      <text
        x={0}
        y={0}
        textAnchor="middle"
        dominantBaseline="central"
        fill={QUESTION_COLOR}
        fontSize={QUESTION_MARK_SIZE * 0.7}
        fontWeight="bold"
        fontFamily="system-ui, sans-serif"
      >
        ?
      </text>
    </g>
  );
};

// =============================================================================
// MAIN COMPONENT
// =============================================================================

export const SparsePointsRenderer: React.FC<SparsePointsRendererProps> = ({
  line,
  isHorizontal,
  domain,
  lineLength,
  lineX,
  lineY,
  scale,
  unitSuffix,
  numberFormat,
  labelPosition,
  onLabelClick,
  onTickContextMenu,
}) => {
  const dispatch = useDispatch();

  // Get dark mode preference
  const isDark = useSelector((state: RootState) => state.preferences.theme === 'dark');
  const locale = useSelector(selectLocale);

  // Get scale factor for secondary line value conversion
  const secondaryLine = useSelector((state: RootState) => state.secondaryLine);
  const scaleFactor = line === 'secondary' ? secondaryLine.scaleFactor : undefined;

  // Get custom ticks for this line
  const customTicks = useSelector((state: RootState) => 
    state.dnlTools.sparseMode.customTicks.filter(
      tick => (tick.line === line || tick.line === 'both') &&
              tick.value >= domain.start &&
              tick.value <= domain.end
    )
  );

  // Check if sparse mode is enabled
  const sparseModeEnabled = useSelector((state: RootState) => 
    state.dnlTools.sparseMode.enabled
  );

  // Get current view and animations state for transitions
  const currentView = useSelector((state: RootState) => 
    state.dnlTools.alternativeViews.currentView
  );
  const animationsEnabled = useSelector((state: RootState) => 
    state.dnlTools.alternativeViews.animationsEnabled
  );

  // Track view changes for animations
  const [isTransitioning, setIsTransitioning] = useState(false);
  const prevViewRef = useRef(currentView);

  // Watch for view changes
  useEffect(() => {
    if (animationsEnabled && prevViewRef.current !== currentView) {
      setIsTransitioning(true);
      prevViewRef.current = currentView;
      
      // Reset transition state after animation completes
      const timer = setTimeout(() => {
        setIsTransitioning(false);
      }, 300);
      
      return () => clearTimeout(timer);
    }
  }, [currentView, animationsEnabled]);

  // Convert value to pixel position
  const getPixelPosition = useCallback((value: number) => {
    return valueToPx(value, domain, lineLength);
  }, [domain, lineLength]);

  // Handle label click
  const handleLabelClick = useCallback((tickId: string, e: React.MouseEvent) => {
    e.stopPropagation();
    if (onLabelClick) {
      onLabelClick(tickId, e);
    } else {
      // Default: toggle label visibility
      dispatch(toggleCustomTickLabelVisible(tickId));
    }
  }, [dispatch, onLabelClick]);

  // Handle context menu
  const handleContextMenu = useCallback((tickId: string, e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();
    if (onTickContextMenu) {
      onTickContextMenu(tickId, e);
    }
  }, [onTickContextMenu]);

  // Render ticks
  const renderedTicks = useMemo(() => {
    return customTicks.map(tick => {
      const pos = getPixelPosition(tick.value);
      const x = isHorizontal ? pos : lineX;
      const y = isHorizontal ? lineY : pos;

      // Skip if position is invalid
      if (isNaN(pos)) return null;

      // Determine if we should show the label:
      // - If there's a custom label (tick.label is a non-empty string)
      // - If it's a question mark (tick.style === 'question')
      const shouldShowLabel = tick.showLabel && (
        (tick.label && tick.label.trim() !== '') || // Has custom label (non-empty)
        tick.style === 'question' // Is question mark
      );
      
      // Format label only if we're going to show it
      const label = shouldShowLabel ? formatTickLabel(tick, unitSuffix, numberFormat, line, scaleFactor, locale) : '';

      return (
        <g
          key={tick.id}
          className={`sparse-tick dnl-custom-tick custom-tick ${isTransitioning ? 'transitioning' : ''}`}
          style={{
            transition: isTransitioning ? 'all 0.3s ease-in-out' : 'none',
            opacity: isTransitioning ? 0.7 : 1,
          }}
          onContextMenu={e => {
            e.preventDefault();
            e.stopPropagation();
            handleContextMenu(tick.id, e);
          }}
        >
          {/* Tick mark */}
          {tick.showTick && (
            <TickMark
              x={x}
              y={y}
              isHorizontal={isHorizontal}
              style={tick.style}
              colour={tick.colour}
              isDark={isDark}
            />
          )}

          {/* Label - show if shouldShowLabel is true */}
          {tick.showLabel && tick.style === 'question' ? (
            <QuestionMarkLabel
              x={x}
              y={y}
              isHorizontal={isHorizontal}
              labelPosition={labelPosition}
              onClick={e => handleLabelClick(tick.id, e)}
            />
          ) : shouldShowLabel && label && (
            <g
              onClick={e => handleLabelClick(tick.id, e)}
              style={{ cursor: 'pointer' }}
            >
              <foreignObject
                x={isHorizontal ? x - 30 : x + (labelPosition === 'above' ? -LABEL_OFFSET : LABEL_OFFSET) - 30}
                y={isHorizontal ? y + (labelPosition === 'above' ? -LABEL_OFFSET : LABEL_OFFSET) - 10 : y - 10}
                width="60"
                height={line === 'primary' && unitSuffix.enabled && unitSuffix.text && !tick.label ? "36" : "20"}
                style={{ pointerEvents: 'none' }}
              >
                {line === 'primary' && unitSuffix.enabled && unitSuffix.text && !tick.label ? (
                  // For primary line with unit suffix, show number above unit
                  <div style={{
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'center',
                    justifyContent: 'center',
                    width: '100%',
                    height: '100%',
                    fontSize: '14px',
                    fontWeight: 500,
                    color: tick.colour || (isDark ? NORMAL_COLOR_DARK : NORMAL_COLOR),
                    lineHeight: '1.2',
                    gap: '2px'
                  }}>
                    {/* Number on top */}
                    <div>
                      <MathRenderer expression={tick.value.toFixed(4).replace(/\.?0+$/, '')} />
                    </div>
                    {/* Unit suffix below */}
                    <div style={{ fontSize: '12px', opacity: 0.8 }}>
                      {unitSuffix.text}
                    </div>
                  </div>
                ) : (
                  <div style={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    width: '100%',
                    height: '100%',
                    fontSize: '14px',
                    fontWeight: 500,
                    color: tick.colour || (isDark ? NORMAL_COLOR_DARK : NORMAL_COLOR)
                  }}>
                    <MathRenderer expression={label} />
                  </div>
                )}
              </foreignObject>
            </g>
          )}
        </g>
      );
    });
  }, [customTicks, getPixelPosition, isHorizontal, lineX, lineY, unitSuffix, numberFormat, labelPosition, isDark, handleLabelClick, handleContextMenu, scaleFactor, line, isTransitioning, locale]);

  // Don't render if sparse mode is not enabled
  if (!sparseModeEnabled) {
    return null;
  }

  return (
    <g className={`sparse-points-layer sparse-points-${line}`}>
      {renderedTicks}
    </g>
  );
};

export default SparsePointsRenderer;
